/**
 * Recipient Search URL State Management
 *
 * Syncs Zustand store state with URL query parameters for:
 * - Shareable links
 * - Browser back/forward navigation
 * - Persistence across page refreshes
 *
 * Architecture: One-way sync (Zustand → URL)
 * - On mount: Hydrate Zustand from URL (once)
 * - On Zustand change: Write to URL via subscription
 */
import { useEffect, useRef } from 'react'

import { useRouter } from 'next/router'
import type { ParsedUrlQuery } from 'querystring'
import { shallow } from 'zustand/shallow'

import { useRecipientSearchStore } from '@/stores/RecipientSearchStore'

import type {
  CertificationType,
  IdentifierType,
  RecipientSearchFilters,
  RecipientSortOption,
} from '@/types/recipient-search'

// Certification keys for URL encoding
const CERTIFICATION_KEYS: CertificationType[] = [
  'woman_owned',
  'veteran_owned',
  'sdvosb',
  'is_8a',
  'hubzone',
  'sdb',
  'minority_owned',
  'small_business',
  'alaska_native',
  'native_american',
  'nonprofit',
  'educational',
]

// Short aliases for URL params (keeps URLs cleaner)
const CERT_URL_ALIASES: Record<CertificationType, string> = {
  woman_owned: 'wosb',
  veteran_owned: 'vosb',
  sdvosb: 'sdvosb',
  is_8a: '8a',
  hubzone: 'hubzone',
  sdb: 'sdb',
  minority_owned: 'mbe',
  small_business: 'sb',
  alaska_native: 'anc',
  native_american: 'nao',
  nonprofit: 'npo',
  educational: 'edu',
}

// Reverse mapping for parsing
const CERT_ALIAS_TO_KEY: Record<string, CertificationType> = Object.fromEntries(
  Object.entries(CERT_URL_ALIASES).map(([key, alias]) => [
    alias,
    key as CertificationType,
  ]),
) as Record<string, CertificationType>

// Valid sort options (Set for O(1) lookup)
const VALID_SORT_OPTIONS = new Set<RecipientSortOption>([
  'relevance',
  'spending_desc',
  'spending_asc',
  'awards_desc',
  'recent_activity',
  'name_asc',
])

// Valid identifier types (Set for O(1) lookup)
const VALID_IDENTIFIER_TYPES = new Set<IdentifierType>(['uei', 'duns', 'cage'])

/**
 * Parse URL query params into filter state
 */
function parseUrlToFilters(query: ParsedUrlQuery): {
  filters: Partial<RecipientSearchFilters>
  query: string
  identifierType: IdentifierType | null
  parentRecipient?: string
  parentRecipientName?: string
} {
  const filters: Partial<RecipientSearchFilters> = {}
  let searchQuery = ''
  let identifierType: IdentifierType | null = null
  let parentRecipient: string | undefined
  let parentRecipientName: string | undefined

  // Text search
  if (typeof query.q === 'string' && query.q.trim()) {
    searchQuery = query.q.trim()
    filters.query = searchQuery
  }

  // Identifier type
  if (
    typeof query.idType === 'string' &&
    VALID_IDENTIFIER_TYPES.has(query.idType as IdentifierType)
  ) {
    identifierType = query.idType as IdentifierType
    filters.identifier_type = identifierType
  }

  // States (array)
  if (query.states) {
    const states = Array.isArray(query.states) ? query.states : [query.states]
    filters.states = states.filter((s): s is string => typeof s === 'string')
  }

  // NAICS codes (array)
  if (query.naics) {
    const naics = Array.isArray(query.naics) ? query.naics : [query.naics]
    filters.naics_codes = naics.filter(
      (n): n is string => typeof n === 'string',
    )
  }

  // PSC codes (array)
  if (query.psc) {
    const psc = Array.isArray(query.psc) ? query.psc : [query.psc]
    filters.psc_codes = psc.filter((p): p is string => typeof p === 'string')
  }

  // Agencies (array)
  if (query.agencies) {
    const agencies = Array.isArray(query.agencies)
      ? query.agencies
      : [query.agencies]
    filters.agencies = agencies.filter(
      (a): a is string => typeof a === 'string',
    )
  }

  // Certifications (comma-separated string)
  if (typeof query.certs === 'string' && query.certs.trim()) {
    const certAliases = query.certs
      .split(',')
      .map((c) => c.trim().toLowerCase())
    for (const alias of certAliases) {
      const certKey = CERT_ALIAS_TO_KEY[alias]
      if (certKey) {
        filters[certKey] = true
      }
    }
  }

  // Spending range
  if (typeof query.minSpend === 'string') {
    const min = parseInt(query.minSpend, 10)
    if (!isNaN(min)) filters.min_spending = min
  }
  if (typeof query.maxSpend === 'string') {
    const max = parseInt(query.maxSpend, 10)
    if (!isNaN(max)) filters.max_spending = max
  }

  // Awards range
  if (typeof query.minAwards === 'string') {
    const min = parseInt(query.minAwards, 10)
    if (!isNaN(min)) filters.min_awards = min
  }
  if (typeof query.maxAwards === 'string') {
    const max = parseInt(query.maxAwards, 10)
    if (!isNaN(max)) filters.max_awards = max
  }

  // Last award date range
  if (typeof query.minDate === 'string' && query.minDate.trim()) {
    filters.min_last_award_date = query.minDate.trim()
  }
  if (typeof query.maxDate === 'string' && query.maxDate.trim()) {
    filters.max_last_award_date = query.maxDate.trim()
  }

  // Parent recipient filter
  if (typeof query.parent === 'string' && query.parent.trim()) {
    parentRecipient = query.parent.trim()
    filters.parent_recipient_hash = parentRecipient
  }
  if (typeof query.parentName === 'string' && query.parentName.trim()) {
    parentRecipientName = query.parentName.trim()
  }

  // Has parent filter
  if (query.hasParent === 'true') {
    filters.has_parent = true
  } else if (query.hasParent === 'false') {
    filters.has_parent = false
  }

  // Sort
  if (
    typeof query.sort === 'string' &&
    VALID_SORT_OPTIONS.has(query.sort as RecipientSortOption)
  ) {
    filters.sort = query.sort as RecipientSortOption
  }

  return {
    filters,
    query: searchQuery,
    identifierType,
    parentRecipient,
    parentRecipientName,
  }
}

/**
 * Serialize filter state to URL search params
 * Omits default/empty values to keep URLs clean
 */
function serializeFiltersToUrl(
  filters: RecipientSearchFilters,
  query: string,
  identifierType: IdentifierType | null,
  parentRecipientName: string | undefined,
): URLSearchParams {
  const params = new URLSearchParams()

  // Text search
  if (query.trim()) {
    params.set('q', query.trim())
  }

  // Identifier type
  if (identifierType) {
    params.set('idType', identifierType)
  }

  // States
  if (filters.states.length > 0) {
    for (const state of filters.states) {
      params.append('states', state)
    }
  }

  // NAICS codes
  if (filters.naics_codes.length > 0) {
    for (const code of filters.naics_codes) {
      params.append('naics', code)
    }
  }

  // PSC codes
  if (filters.psc_codes.length > 0) {
    for (const code of filters.psc_codes) {
      params.append('psc', code)
    }
  }

  // Agencies
  if (filters.agencies.length > 0) {
    for (const agency of filters.agencies) {
      params.append('agencies', agency)
    }
  }

  // Certifications (comma-separated for compactness)
  const activeCerts: string[] = []
  for (const certKey of CERTIFICATION_KEYS) {
    if (filters[certKey] === true) {
      activeCerts.push(CERT_URL_ALIASES[certKey])
    }
  }
  if (activeCerts.length > 0) {
    params.set('certs', activeCerts.join(','))
  }

  // Spending range
  if (filters.min_spending !== undefined) {
    params.set('minSpend', String(filters.min_spending))
  }
  if (filters.max_spending !== undefined) {
    params.set('maxSpend', String(filters.max_spending))
  }

  // Awards range
  if (filters.min_awards !== undefined) {
    params.set('minAwards', String(filters.min_awards))
  }
  if (filters.max_awards !== undefined) {
    params.set('maxAwards', String(filters.max_awards))
  }

  // Last award date range
  if (filters.min_last_award_date) {
    params.set('minDate', filters.min_last_award_date)
  }
  if (filters.max_last_award_date) {
    params.set('maxDate', filters.max_last_award_date)
  }

  // Parent recipient
  if (filters.parent_recipient_hash) {
    params.set('parent', filters.parent_recipient_hash)
    if (parentRecipientName) {
      params.set('parentName', parentRecipientName)
    }
  }

  // Has parent filter
  if (filters.has_parent !== undefined) {
    params.set('hasParent', String(filters.has_parent))
  }

  // Sort (only if not default)
  if (filters.sort && filters.sort !== 'relevance') {
    params.set('sort', filters.sort)
  }

  return params
}

/**
 * Hook to sync recipient search state with URL
 *
 * Call this once at the page level. It:
 * 1. Hydrates Zustand from URL on mount
 * 2. Subscribes to Zustand changes and updates URL
 */
export function useRecipientSearchUrl(): void {
  const router = useRouter()
  const isHydrated = useRef(false)
  const isUpdatingUrl = useRef(false)

  // Get store actions (stable references)
  const hydrateFromUrl = useRecipientSearchStore((s) => s.hydrateFromUrl)

  // Get store actions for clearing parent recipient
  const clearParentRecipient = useRecipientSearchStore(
    (s) => s.clearParentRecipient,
  )

  // Hydrate from URL on mount (once, after router is ready)
  useEffect(() => {
    if (isHydrated.current || !router.isReady) return
    isHydrated.current = true

    const parsed = parseUrlToFilters(router.query)

    // Skip if no URL params to hydrate
    if (
      Object.keys(parsed.filters).length === 0 &&
      !parsed.query &&
      !parsed.identifierType
    ) {
      return
    }

    // Single batched update to avoid multiple re-renders
    hydrateFromUrl(parsed)
  }, [router.isReady, router.query, hydrateFromUrl])

  // Handle browser back/forward navigation
  // When URL changes via popstate, sync store with new URL state
  useEffect(() => {
    if (!router.isReady) return

    const handleRouteChange = (url: string) => {
      // Skip if this is a programmatic navigation (we're updating the URL)
      if (isUpdatingUrl.current) return

      // Parse the new URL to check for parent recipient
      const urlObj = new URL(url, window.location.origin)
      const hasParent = urlObj.searchParams.has('parent')

      // If navigating to a URL without parent param but store has parent,
      // clear the parent recipient (user pressed back)
      const currentState = useRecipientSearchStore.getState()
      if (!hasParent && currentState.parentRecipient) {
        clearParentRecipient()
      }
    }

    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.isReady, router.events, clearParentRecipient])

  // Subscribe to Zustand changes and update URL
  // Use primitive dependencies to avoid effect re-runs (rerender-dependencies)
  const pathname = router.pathname
  const isReady = router.isReady

  useEffect(() => {
    // Wait for hydration before subscribing
    if (!isReady) return

    // Track previous state for shallow comparison (single getState call)
    const initialState = useRecipientSearchStore.getState()
    let prevState = {
      filters: initialState.filters,
      query: initialState.query,
      identifierType: initialState.identifierType,
      parentRecipientName: initialState.parentRecipientName,
    }

    const unsubscribe = useRecipientSearchStore.subscribe((state) => {
      const currentState = {
        filters: state.filters,
        query: state.query,
        identifierType: state.identifierType,
        parentRecipientName: state.parentRecipientName,
      }

      // Skip if state hasn't changed (shallow compare)
      if (shallow(prevState, currentState)) return

      // Detect if we're entering subsidiary view (parent was empty, now has value)
      const enteringSubsidiaryView =
        !prevState.filters.parent_recipient_hash &&
        !!currentState.filters.parent_recipient_hash

      prevState = currentState

      // Skip if we're in the middle of a URL update (prevents loops)
      if (isUpdatingUrl.current) return

      const params = serializeFiltersToUrl(
        state.filters,
        state.query,
        state.identifierType,
        state.parentRecipientName,
      )

      const newUrl = params.toString()
        ? `${pathname}?${params.toString()}`
        : pathname

      // Only update if URL actually changed (compare with current window URL)
      const currentUrl = window.location.pathname + window.location.search
      const targetUrl = newUrl.startsWith('/') ? newUrl : `/${newUrl}`
      if (targetUrl !== currentUrl) {
        isUpdatingUrl.current = true
        // Use push when entering subsidiary view so back button works
        // Use replace for all other filter changes to avoid cluttering history
        const navigate = enteringSubsidiaryView ? router.push : router.replace
        navigate(newUrl, undefined, { shallow: true }).finally(() => {
          isUpdatingUrl.current = false
        })
      }
    })

    return unsubscribe
  }, [isReady, pathname, router])
}
